import { useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationKeys } from '@/services/queryKeys';
import { notificationService } from '@/services/notification.service';

/** Poll dự phòng khi CHƯA có socket realtime: 60s (hành vi cũ, giữ nguyên). */
export const UNREAD_POLL_INTERVAL_MS = 60_000;
/**
 * Socket đã kết nối → mỗi `notification.created`/`notification.read` đã tự invalidate, nên
 * unread-count chỉ cần poll thưa để chống lệch trạng thái (mất event, tab ngủ rồi thức).
 * P4: trước đây mỗi tab vẫn poll 60s bất kể socket → ~1.400 req/loại trên APM prod.
 */
export const UNREAD_POLL_INTERVAL_WITH_SOCKET_MS = 300_000;

/**
 * Khoảng poll unread-count theo trạng thái socket. Tách hàm thuần để test được độc lập với hook
 * (và để component/khác dùng lại mà không phải tự nhớ con số).
 */
export function unreadPollIntervalMs(socketConnected: boolean): number {
  return socketConnected ? UNREAD_POLL_INTERVAL_WITH_SOCKET_MS : UNREAD_POLL_INTERVAL_MS;
}

/**
 * Trạng thái kết nối của socket notification, chia sẻ giữa người GHI (`useNotificationSocket`,
 * mount 1 lần ở Layout) và người ĐỌC (`NotificationBell`) mà không cần prop drilling qua
 * Layout → Header → Bell.
 *
 * Vì sao không dùng Redux: đây là trạng thái runtime của 1 kết nối, không phải dữ liệu ứng dụng;
 * và `useNotificationSocket` nằm ngoài luồng dispatch. Store tối giản + `useSyncExternalStore`
 * cho đúng ngữ nghĩa "external store" của React 18 (đọc đồng bộ, không tearing).
 */
let notificationSocketConnected = false;
const socketConnectedListeners = new Set<() => void>();

function subscribeSocketConnected(listener: () => void): () => void {
  socketConnectedListeners.add(listener);
  return () => {
    socketConnectedListeners.delete(listener);
  };
}

function getSocketConnectedSnapshot(): boolean {
  return notificationSocketConnected;
}

/** Chỉ `useNotificationSocket` gọi hàm này (connect/disconnect/cleanup). */
export function setNotificationSocketConnected(connected: boolean): void {
  if (notificationSocketConnected === connected) return;
  notificationSocketConnected = connected;
  for (const listener of socketConnectedListeners) listener();
}

/** Component đọc trạng thái: `true` = realtime đang chạy → poll unread thưa (300s). */
export function useNotificationSocketConnected(): boolean {
  return useSyncExternalStore(subscribeSocketConnected, getSocketConnectedSnapshot);
}

/**
 * Danh sách thông báo gần nhất.
 *
 * P4: list KHÔNG còn poll nền 60s. Chuông chỉ tải list khi mở dropdown (`enabled: open`), và
 * không dùng `refetchInterval` — có thông báo mới thì socket invalidate query (xem
 * `useNotificationSocket`), mở lại dropdown là có dữ liệu mới. Nhờ vậy mỗi tab không còn gọi
 * `GET /v1/notifications` khi người dùng không nhìn.
 *
 * Mặc định `enabled: true` để màn `/notifications` (nơi list là nội dung chính) vẫn chạy như cũ.
 */
export function useNotificationList(scope?: 'mine', options: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    queryKey: [...notificationKeys.lists(), scope ?? 'all'],
    queryFn: () => notificationService.list(scope),
    enabled: options.enabled ?? true,
    // Chỉ chuông (`poll: false`) bỏ poll — nguồn cập nhật của nó là socket + mở dropdown. Màn
    // `/notifications` và shipper app (không mount socket) giữ poll 60s như trước.
    refetchInterval: options.poll === false ? false : 60_000,
    // Tường minh: kể cả sau này ai thêm refetchInterval thì tab ẩn cũng không gọi nền.
    refetchIntervalInBackground: false,
    retry: 0,
  });
}

/**
 * Số thông báo chưa đọc cho badge của chuông.
 *
 * `socketConnected`: khi socket realtime đã nối, sự kiện mới tự invalidate nên interval giãn lên
 * 300s; khi chưa nối (BE chưa bật gateway, WS lỗi, hoặc shipper app không mount socket) giữ 60s
 * để badge không lệch quá lâu.
 */
export function useUnreadCount(scope?: 'mine', enabled = true, socketConnected = false) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(scope),
    queryFn: () => notificationService.unreadCount(scope),
    enabled,
    refetchInterval: unreadPollIntervalMs(socketConnected),
    // Tường minh: tab ẩn không poll nền — mỗi tab là một nguồn tải trên BE.
    refetchIntervalInBackground: false,
    retry: 0,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllRead(scope?: 'mine') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.markAllRead(scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
